import clsx from 'clsx'
import React, { Suspense, useEffect, useState } from 'react'

import { useDeleteFavouritePractitioner } from 'api/querries/favourite/useFavouritePractitionerDelete'
import { usePostFavouritePractitioner } from 'api/querries/favourite/useFavouritePractitionerPost'
import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg'
import { ReactComponent as FavouritesFilled } from 'assets/icons/favourites-filled.svg'
import { ReactComponent as Favourites } from 'assets/icons/favourites.svg'
import { ReactComponent as MailIcon } from 'assets/icons/mail.svg'
import magnifyingTool from 'assets/images/practitionersList/magnifyingTool.png'
import Bubble from 'components/bubble/Bubble'
import PractitionersListDetailedView from 'components/practitionersListDetailedView/PractitionersListDetailedView'
import TruncatedText from 'components/truncatedText/TruncatedText'
import Avatar from 'components/uikit/Avatar'
import { Icon } from 'components/uikit/Icon/Icon'
import { Tooltip } from 'components/uikit/Tooltip'
import useWindowWidth from 'hooks/useWindowWidth'
import { AnalyticsActionType } from 'types/analytics.type'
import { Practitioner } from 'types/practitioners'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './PractitionersList.module.scss'
import { TableSort } from './PractitionersSort'

export interface Header {
  title: string
  key: string
  sortable?: boolean
  sortingKey?: string
  className?: string
}

interface Props {
  headers: Header[]
  items?: Practitioner[]
  className?: string
  onHeaderClick?: (T: Sort) => void
  isLoading?: boolean
  onFiltersClear?: () => void
  hasFilters?: boolean
}

export interface Sort {
  orientation?: 'ASC' | 'DESC' | undefined
  sortingKey?: string
}

const MAX_VISIBLE_COMMUNITY_TAGS = 2

const PractitionersList = ({
  headers,
  items,
  className,
  onHeaderClick,
  isLoading,
  onFiltersClear,
  hasFilters,
}: Props) => {
  const [expanded, setExpanded] = useState<undefined | number[]>()

  const { mutateAsync: postFavourite } = usePostFavouritePractitioner()
  const { mutateAsync: deleteFavourite } = useDeleteFavouritePractitioner()

  const handleExpandRow = (index: number) => {
    setExpanded((prevState = []) => {
      if (prevState.includes(index)) {
        return prevState.filter(p => p !== index)
      } else {
        return [...prevState, index]
      }
    })
  }

  const [activeSorting, setActiveSorting] = useState<Sort>({ orientation: undefined, sortingKey: undefined })
  const handleSortClick = (header: Header) => {
    if (!header.sortable) return

    const currentOrientation =
      activeSorting.orientation === undefined ? 'DESC' : activeSorting.orientation === 'DESC' ? 'ASC' : undefined

    setActiveSorting({
      sortingKey: currentOrientation ? header.sortingKey : undefined,
      orientation: currentOrientation,
    })
  }

  // Collapse all accordions upon new data
  useEffect(() => setExpanded(undefined), [items])

  useEffect(() => {
    onHeaderClick && onHeaderClick(activeSorting)
    // eslint-disable-next-line
  }, [activeSorting])

  const generateCommunityTagsTooltip = (communityTags: Practitioner['practitionerCommunityTags']) => {
    return communityTags.slice(MAX_VISIBLE_COMMUNITY_TAGS, communityTags.length).map(ct => (
      <div key={ct?.id}>
        <span>{ct?.tag?.name}</span>
        <br />
      </div>
    ))
  }

  const toggleFavouritePractitioner = async ({
    practitionerId,
    remove,
  }: {
    practitionerId: string
    remove?: boolean
  }) => {
    remove ? await deleteFavourite({ practitionerId }) : await postFavourite({ practitionerId })
  }

  const windowWidth = useWindowWidth()

  return (
    <div className={clsx(styles.practitionersList, className)}>
      <div className={styles.header}>
        {headers?.map((header, index) => (
          <div
            key={`header-${index}`}
            className={clsx(styles.headerItem, header?.className)}
            onClick={() => handleSortClick(header)}
          >
            {header.title}
            {header.sortable && (
              <TableSort
                orientation={header.sortingKey === activeSorting.sortingKey ? activeSorting.orientation : undefined}
              />
            )}
          </div>
        ))}
      </div>
      <div className={styles.list}>
        <Suspense fallback={<div>Loading...</div>}>
          {!isLoading && items?.length ? (
            items.map(
              (
                {
                  id,
                  user,
                  practitionerCommunityTags,
                  favourites,
                  highLevelSkills,
                  highLevelExperiences,
                  jobFamilyGroup,
                  jobFamilyName,
                  company,
                },
                index,
                array,
              ) => (
                <React.Fragment key={index}>
                  <div className={styles.row} key={`row-${index}`}>
                    <div
                      className={clsx(
                        styles.rowItem,
                        styles.centered,
                        'cursor-pointer',
                        'tour-communityofpractitioners-viewskillsandexperiences',
                      )}
                      onClick={() => handleExpandRow(index)}
                    >
                      <Icon
                        icon={ChevronDown}
                        className={clsx(styles.centered, styles.chevron, 'rotate-90 transition-all', {
                          [styles.chevronOpen]: expanded?.includes(index),
                        })}
                        color="black"
                      />
                    </div>
                    <div className={clsx(styles.rowItem, styles.nameItem)}>
                      <Avatar
                        className={styles.avatar}
                        name={`${user?.firstname} ${user?.lastname}`}
                        src={user?.image}
                        size="m"
                        // withTooltip
                      />
                      <div className={styles.nameInnerItem}>
                        <div className={styles.nameInnerItem}>
                          {user?.visibilityPreference?.preference === 'public' ||
                          (user?.visibilityPreference?.preference === 'partial' && !!user?.whitelistedCaller) ? (
                            <div
                              className={clsx(styles.nameActive, 'cursor-pointer')}
                              onClick={() =>
                                window.open(
                                  `${process.env.REACT_APP_EXPERIENCE_PROFILE_BASE_URL}/users/${user?.email}/public`,
                                )
                              }
                            >
                              {user?.firstname}
                              <br /> {user?.lastname}
                            </div>
                          ) : (
                            <div className={styles.nameInactive}>
                              {user?.firstname}
                              <br /> {user?.lastname}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={clsx(styles.rowItem)}>
                      <div className={styles.textTruncate}>{user?.company || company?.name}</div>
                    </div>
                    <div className={clsx(styles.rowItem, styles.communityRow)}>
                      {practitionerCommunityTags?.length > MAX_VISIBLE_COMMUNITY_TAGS ? (
                        <>
                          {practitionerCommunityTags
                            ?.slice(0, MAX_VISIBLE_COMMUNITY_TAGS)
                            ?.map(({ tag }, index) => (
                              <TruncatedText
                                key={`ct-${index}`}
                                className={clsx(styles.communityItem)}
                                text={tag?.name}
                                wrapperMaxWidth={windowWidth < 1440 ? '120px' : windowWidth < 1660 ? '130px' : '168px'}
                                maxWidth={windowWidth < 1440 ? '110px' : windowWidth < 1660 ? '120px' : '160px'}
                              />
                            ))}

                          <div
                            key={`truncatedTag-${index}`}
                            className={clsx(styles.communityItem, styles.textTruncate, styles.communityTagTooltip)}
                          >
                            <Tooltip config={{ allowHTML: true }}>
                              {`+ ${practitionerCommunityTags?.length - MAX_VISIBLE_COMMUNITY_TAGS}`}
                              <div className={styles.tooltipContent} slot="tooltip-content">
                                {generateCommunityTagsTooltip(practitionerCommunityTags)}
                              </div>
                            </Tooltip>
                          </div>
                        </>
                      ) : (
                        practitionerCommunityTags?.map(({ tag }, index) => (
                          <TruncatedText
                            key={`ct-${index}`}
                            className={clsx(styles.communityItem)}
                            text={tag?.name}
                            wrapperMaxWidth={windowWidth < 1440 ? '120px' : windowWidth < 1660 ? '130px' : '168px'}
                            maxWidth={windowWidth < 1440 ? '110px' : windowWidth < 1660 ? '120px' : '160px'}
                          />
                        ))
                      )}
                    </div>
                    <div className={clsx(styles.rowItem)}>
                      <TruncatedText
                        text={user?.jobFamilyName || jobFamilyName?.name}
                        maxWidth={windowWidth < 1440 ? '110px' : windowWidth < 1560 ? '135px' : '150px'}
                      />
                    </div>
                    <div className={clsx(styles.rowItem)}>
                      <TruncatedText
                        text={user?.jobFamilyGroup || jobFamilyGroup?.name}
                        maxWidth={windowWidth < 1440 ? '110px' : windowWidth < 1560 ? '135px' : '150px'}
                      />
                    </div>
                    <div className={clsx(styles.rowItem)}>
                      <Tooltip config={{ allowHTML: true }}>
                        {user?.country?.cluster?.code}
                        <div slot="tooltip-content" className={styles.tooltipContent}>
                          {user?.country?.cluster?.name}
                        </div>
                      </Tooltip>
                    </div>
                    <div className={clsx(styles.rowItem)}>
                      <TruncatedText
                        text={user?.operatingUnit?.name}
                        maxWidth={windowWidth < 1440 ? '80px' : windowWidth < 1560 ? '105px' : '120px'}
                      />
                    </div>
                    <div
                      className={clsx(
                        'tour-communityofpractitioners-contactpracititioner',
                        styles.rowItem,
                        styles.centered,
                      )}
                    >
                      <a
                        href={`mailto:${user?.email}`}
                        onClick={() =>
                          trackAnalytics({
                            type: AnalyticsActionType.action,
                            payload: {
                              action: ANALYTICS_EVENTS.PRACTITIONERS_LIST_EMAIL_CLICKED(
                                `${user?.firstname}_${user?.lastname}`,
                              ),
                              params: [
                                {
                                  key: 'practitioner_name',
                                  value: `${user?.firstname} ${user?.lastname}`,
                                },
                                {
                                  key: 'company',
                                  value: user?.company || company?.name || '',
                                },
                                {
                                  key: 'community',
                                  value: practitionerCommunityTags?.map(ct => ct.tag.name)?.join(', ') || '',
                                },
                                {
                                  key: 'jobFamily',
                                  value: user?.jobFamilyGroup || jobFamilyName?.name || '',
                                },
                                {
                                  key: 'department',
                                  value: user?.jobFamilyName || jobFamilyName?.name || '',
                                },
                                {
                                  key: 'cluster',
                                  value: user?.country?.cluster?.code || '',
                                },
                                { key: 'contact', value: user?.email || '' },
                                {
                                  key: 'is-favourite',
                                  value: favourites?.length ? 'yes' : 'no',
                                },
                              ],
                            },
                          })
                        }
                      >
                        <Icon size={17} icon={MailIcon} color="#89919a" />
                      </a>
                    </div>
                    <div
                      className={clsx(
                        'tour-communityofpractitioners-favoritepractitioners',
                        styles.rowItem,
                        styles.centered,
                        styles.favourite,
                      )}
                    >
                      {favourites?.length ? (
                        <Icon
                          icon={FavouritesFilled}
                          color="black"
                          onClick={() => {
                            toggleFavouritePractitioner({ practitionerId: id, remove: true })
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: {
                                action: ANALYTICS_EVENTS.PRACTITIONERS_LIST_FAVORITE_TOGGLE_CLICKED(
                                  `${user?.firstname}_${user?.lastname}`,
                                ),
                                params: [
                                  {
                                    key: 'practitioner_name',
                                    value: `${user?.firstname} ${user?.lastname}`,
                                  },
                                  {
                                    key: 'company',
                                    value: user?.company || company?.name || '',
                                  },
                                  {
                                    key: 'community',
                                    value: practitionerCommunityTags?.map(ct => ct.tag.name)?.join(', ') || '',
                                  },
                                  {
                                    key: 'jobFamily',
                                    value: user?.jobFamilyGroup || jobFamilyName?.name || '',
                                  },
                                  {
                                    key: 'department',
                                    value: user?.jobFamilyName || jobFamilyName?.name || '',
                                  },
                                  {
                                    key: 'cluster',
                                    value: user?.country?.cluster?.code || '',
                                  },
                                  { key: 'contact', value: user?.email || '' },
                                  {
                                    key: 'is-favourite',
                                    value: 'no',
                                  },
                                ],
                              },
                            })
                          }}
                        />
                      ) : (
                        <Icon
                          icon={Favourites}
                          color="black"
                          onClick={() => {
                            toggleFavouritePractitioner({ practitionerId: id, remove: false })
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: {
                                action: ANALYTICS_EVENTS.PRACTITIONERS_LIST_FAVORITE_TOGGLE_CLICKED(
                                  `${user?.firstname}_${user?.lastname}`,
                                ),
                                params: [
                                  {
                                    key: 'practitioner_name',
                                    value: `${user?.firstname} ${user?.lastname}`,
                                  },
                                  {
                                    key: 'company',
                                    value: user?.company || company?.name || '',
                                  },
                                  {
                                    key: 'community',
                                    value: practitionerCommunityTags?.map(ct => ct.tag.name)?.join(', ') || '',
                                  },
                                  {
                                    key: 'jobFamily',
                                    value: user?.jobFamilyGroup || jobFamilyName?.name || '',
                                  },
                                  {
                                    key: 'department',
                                    value: user?.jobFamilyName || jobFamilyName?.name || '',
                                  },
                                  {
                                    key: 'cluster',
                                    value: user?.country?.cluster?.code || '',
                                  },
                                  { key: 'contact', value: user?.email || '' },
                                  {
                                    key: 'is-favourite',
                                    value: 'yes',
                                  },
                                ],
                              },
                            })
                          }}
                        />
                      )}
                    </div>
                    <PractitionersListDetailedView
                      isOpen={expanded?.includes(index)}
                      highLevelSkills={highLevelSkills || []}
                      highLevelExperiences={highLevelExperiences}
                      email={user?.email}
                      showExpProfileCta={
                        user?.visibilityPreference?.preference === 'public' ||
                        (user?.visibilityPreference?.preference === 'partial' && !!user?.whitelistedCaller)
                      }
                    />
                  </div>
                  {index < array?.length - 1 && (
                    <div key={`divider-${index}`} className={clsx(styles.rowDivider, 'col-span-10')} />
                  )}
                </React.Fragment>
              ),
            )
          ) : isLoading ? (
            <div className={styles.noData}>Loading data...</div>
          ) : (
            <div className={styles.noData}>
              <EmptyTableState onFiltersClear={onFiltersClear} hasFilters={hasFilters} />
            </div>
          )}
        </Suspense>
      </div>
    </div>
  )
}

export default PractitionersList

const EmptyTableState = ({ onFiltersClear, hasFilters }: { onFiltersClear?: () => void; hasFilters?: boolean }) => (
  <div className={styles.content}>
    <div className={styles.iconWrapper}>
      <Bubble
        text="Hmmm it's a mystery"
        color="#EBFF00"
        className={clsx(styles.bubble, 'left-full -top-[50px] w-[210px]')}
        fontClassName="italic font-bold text-[#282828]"
      />
      <img src={magnifyingTool} alt="no-data" />
    </div>
    <div className={styles.heading}>{hasFilters ? <>There are no results that fit your search.</> : <>No data.</>}</div>
    <div className={styles.subheading}>
      {hasFilters ? (
        <>
          <span className={styles.cta} onClick={onFiltersClear}>
            Clear your search
          </span>{' '}
          or try different keywords or filters.
        </>
      ) : (
        <>Please try again later or contact administrator.</>
      )}
    </div>
  </div>
)
